import type { BombiixCellType, BombiixColumnDef, BombiixColumnDefOrGroup, BombiixRow } from './types';

/** Cell types a keyboard highlight may land on — everything that renders a live control. */
const NAVIGABLE_CELL_TYPES: ReadonlySet<BombiixCellType> = new Set<BombiixCellType>(['text', 'currency', 'percent', 'select', 'checkbox', 'radio', 'date']);

/**
 * Whether the active-cell highlight may land on this column for this row.
 *
 * Mirrors the render-time editability rules in BombiixAGGrid's cellRendererSelector: interactive
 * cellType, row present and not a totals footer, and none of grid / row / cell readOnly, disabled
 * or hidden. Kept pure so the navigation policy is unit-testable without an AG Grid instance.
 */
export function isCellNavigable<TRow extends BombiixRow>(
  colDef: BombiixColumnDef<TRow> | undefined,
  row: TRow | undefined,
  gridReadOnly: boolean,
  isRowReadOnly?: (row: TRow) => boolean,
): boolean {
  if (!colDef?.cellType || !NAVIGABLE_CELL_TYPES.has(colDef.cellType)) return false;
  if (!row || row.__cagFooter) return false;
  if (gridReadOnly || (isRowReadOnly ? isRowReadOnly(row) : false)) return false;
  if (colDef.getCellHidden && colDef.getCellHidden(row)) return false;
  if (colDef.getCellReadOnly && colDef.getCellReadOnly(row)) return false;
  if (colDef.getCellDisabled && colDef.getCellDisabled(row)) return false;
  return true;
}

/**
 * Nearest navigable column beside `currentColId` in `direction` (1 = right, -1 = left), or null
 * when none exists before the row edge. Never wraps to another row.
 */
export function findNextNavigableColId<TRow extends BombiixRow>(
  orderedColIds: string[],
  currentColId: string,
  direction: 1 | -1,
  resolveColDef: (colId: string) => BombiixColumnDef<TRow> | undefined,
  row: TRow | undefined,
  gridReadOnly: boolean,
  isRowReadOnly?: (row: TRow) => boolean,
): string | null {
  const start = orderedColIds.indexOf(currentColId);
  if (start === -1) return null;
  for (let i = start + direction; i >= 0 && i < orderedColIds.length; i += direction) {
    const colId = orderedColIds[i];
    if (colId !== undefined && isCellNavigable(resolveColDef(colId), row, gridReadOnly, isRowReadOnly)) return colId;
  }
  return null;
}

/**
 * Nearest row above or below `currentRowIndex` whose cell in this column is navigable
 * (1 = down, -1 = up), or null at the grid's edge. Never wraps.
 *
 * Rows are skipped rather than stopped on, because a column can be hidden or read-only for some
 * rows only — the Costing TODO grid hides an expanded parent's cell and shows it on the child
 * rows beneath, so a stop-on-first-row walk would strand the highlight on the parent.
 */
export function findNextNavigableRowIndex<TRow extends BombiixRow>(
  rowCount: number,
  currentRowIndex: number,
  direction: 1 | -1,
  colDef: BombiixColumnDef<TRow> | undefined,
  getRow: (rowIndex: number) => TRow | undefined,
  gridReadOnly: boolean,
  isRowReadOnly?: (row: TRow) => boolean,
): number | null {
  for (let i = currentRowIndex + direction; i >= 0 && i < rowCount; i += direction) {
    if (isCellNavigable(colDef, getRow(i), gridReadOnly, isRowReadOnly)) return i;
  }
  return null;
}

/** Flattens leaf column defs (unwrapping groups) into a map keyed by `colId ?? field`. */
export function collectLeafColumnDefs<TRow extends BombiixRow>(columnDefs: BombiixColumnDefOrGroup<TRow>[]): Map<string, BombiixColumnDef<TRow>> {
  const map = new Map<string, BombiixColumnDef<TRow>>();
  const addLeaf = (leaf: BombiixColumnDef<TRow>) => {
    const key = leaf.colId ?? leaf.field;
    if (key) map.set(String(key), leaf);
  };
  columnDefs.forEach((entry) => {
    if (entry && 'children' in entry && Array.isArray((entry as { children?: unknown }).children)) {
      (entry as BombiixColumnDefOrGroup<TRow> & { children: BombiixColumnDef<TRow>[] }).children.forEach(addLeaf);
    } else {
      addLeaf(entry as BombiixColumnDef<TRow>);
    }
  });
  return map;
}
